<template>
  <div id="AppMenu">
    <div v-if="currentSeasonToggle">
      <div id="menuWrapper">
        <div class="menuSection" @click="goRouter('/')">
          <div id="logoBlock">
            <img
              @click="refreshLeagueInformation"
              src="https://diamond-public.s3.us-west-2.amazonaws.com/assets/diamond-logo-white.png"
            />
          </div>
        </div>
        <div class="menuSection">
          <p class="menuHeader bold">{{ $t("AppMenu.organization") }}</p>
          <div v-if="editing">
            <Dropdown
              :value="currentOrganizationToggle.id"
              optionLabel="name"
              optionValue="id"
              @input="setCurrentOrganization"
              :options="organizations"
              :placeholder="$t('AppMenu.placeholder.organization')"
              Lkey
              style="width: 100%; margin-bottom: 5px"
            />
          </div>
          <p class="menuHeader bold">{{ $t("AppMenu.league") }}</p>
          <div v-if="editing">
            <Dropdown
              :value="currentLeagueToggle"
              optionLabel="name"
              @input="setCurrentLeague"
              :options="leagues"
              Lkey
              :placeholder="$t('AppMenu.placeholder.league')"
              style="width: 100%; margin-bottom: 5px"
            />
            <p class="menuHeader bold">{{ $t("AppMenu.season") }}</p>
            <Dropdown
              :value="currentSeasonToggle"
              optionLabel="name"
              @input="setCurrentSeason"
              :options="seasons"
              :placeholder="$t('AppMenu.placeholder.season')"
              style="width: 100%"
            />
          </div>
        </div>
        <div v-if="currentSeasonToggle">
          <Dialog
            :showHeader="false"
            :visible.sync="createDialog"
            :style="{ minWidth: '300px', maxWidth: '600px' }"
            :modal="true"
          >
            <!-- Schedule, Team, Person, Division, Venue, Season  -->
            <div class="p-mt-3" style="min-height: 300px">
              <h2 class="title is-3 p-mb-2">
                {{ $t("AppMenu.create_button") }}
                <font-awesome-icon
                  class="menuIcon floatRight cursorpointer"
                  :icon="['fal', 'times-circle']"
                  @click="createDialog = false"
                />
              </h2>
              <div class="p-grid p-mt-3">
                <div class="p-col">
                  <div class="createEntry" @click.stop="goRouter('/schedules/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'calendar-alt']" />
                    <span>{{ $t("AppMenu.schedule") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/tournaments/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'award']" />
                    <span>{{ $t("AppMenu.tournament") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/divisions/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'ball-pile']" />
                    <span>{{ $t("AppMenu.division") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/teams/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'users']" />
                    <span>{{ $t("AppMenu.team") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/venues/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'map-marker-alt']" />
                    <span>{{ $t("AppMenu.venue") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/people/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'user']" />
                    <span>{{ $t("AppMenu.person") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/wizard')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'chart-network']" />
                    <span>{{ $t("AppMenu.league_wizard") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/leagues/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'chart-network']" />
                    <span>{{ $t("AppMenu.manual") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/seasons/new')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'pennant']" />
                    <span>{{ $t("AppMenu.season") }}</span>
                  </div>
                  <div class="createEntry" @click.stop="goRouter('/import/csv')">
                    <font-awesome-icon class="menuIcon" :icon="['fal', 'user']" />
                    <span>{{ $t("AppMenu.csv_import") }}</span>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>
          <div class="menuSection p-grid">
            <!-- <div class="p-col-fixed iconCol"></div> -->
            <div class="p-col">
              <Button
                @click="toggleCreateDialog"
                class="
                  p-button-raised
                  p-button-lg
                  p-button-rounded
                  p-my-2
                  app-menu-create-btn
                "
              >
                <font-awesome-icon class="menuIcon" icon="plus" />
                &nbsp;
                <strong class="uppercase has-text-white">{{ $t("AppMenu.inside_create_button") }}</strong> &nbsp;
              </Button>
            </div>
          </div>
          <div class="menuSection p-grid">
            <!-- <div class="p-col-fixed iconCol"></div> -->
            <div class="p-col" @click="refreshLeagueInformation('/')">
              <router-link to="/" class="menuHeader blueHover" :style="{ background: getBackground('/') }">
                <font-awesome-icon class="menuIcon" :icon="['fal', 'home-alt']" />
                <span class="menuText"> {{ $t("AppMenu.home") }} </span>
              </router-link>
            </div>
          </div>
          <div class="menuSection p-grid">
            <div class="p-col" @click="refreshLeagueInformation('/public-webpage')">
              <router-link
                to="/public-webpage"
                class="menuHeader blueHover"
                :style="{ background: getBackground('/public-webpage') }"
              >
                <font-awesome-icon class="menuIcon" :icon="['fal', 'browser']" />
                <span class="menuText"> {{ $t("AppMenu.webpage") }} </span>
              </router-link>
            </div>
          </div>
          <div class="menuSection p-grid">
            <!-- <div class="p-col-fixed iconCol"></div> -->
            <div class="p-col">
              <a
                @click="refreshLeagueInformation('/schedules')"
                class="menuHeader blueHover"
                :style="{ background: getBackground('/schedules') }"
              >
                <font-awesome-icon class="menuIcon" :icon="['fal', 'gem']" />
                <span class="menuText"> {{ $t("AppMenu.schedules") }} </span>
              </a>
            </div>
          </div>
          <div class="menuSection p-grid">
            <div class="p-col">
              <a
                @click="refreshLeagueInformation('/tournaments')"
                class="menuHeader blueHover"
                :style="{ background: getBackground('/tournaments') }"
              >
                <font-awesome-icon class="menuIcon" :icon="['fal', 'trophy']" />
                <span class="menuText">
                  {{ $t("AppMenu.tournaments") }}
                  <span class="beta-badge">NEW</span>
                </span>
              </a>
            </div>
          </div>
          <div class="menuSection p-grid">
            <!-- <div class="p-col-fixed iconCol"></div> -->
            <div class="p-col">
              <router-link to="/calendar" class="menuHeader blueHover">
                <font-awesome-icon class="menuIcon" :icon="['fal', 'calendar-alt']" />
                <span class="menuText"> {{ $t("AppMenu.calendar") }} </span>
              </router-link>
            </div>
          </div>
          <div class="menuSection p-grid">
            <div class="p-col" @click="goRouter('list')">
              <router-link to="/list" class="menuHeader blueHover" :style="{ background: getBackground('/list') }">
                <font-awesome-icon class="menuIcon" :icon="['fal', 'clipboard-list']" />
                <span class="menuText"> {{ $t("AppMenu.game") }} </span>
              </router-link>
            </div>
          </div>
          <div class="menuSection p-grid">
            <div class="p-col" @click="goRouter('standings')">
              <router-link
                to="/standings"
                class="menuHeader blueHover"
                :style="{ background: getBackground('/standings') }"
              >
                <font-awesome-icon class="menuIcon" :icon="['fat', 'ranking-star']" />
                <span class="menuText">
                  {{ $t("AppMenu.standing") }}
                  <span class="beta-badge">NEW</span>
                </span>
              </router-link>
            </div>
          </div>
          <div class="menuSection p-grid">
            <div class="p-col" @click="goRouter('league-management')">
              <router-link
                to="/league-management"
                class="menuHeader blueHover"
                :style="{ background: getBackground('/league-management') }"
              >
                <font-awesome-icon class="menuIcon" :icon="['fat', 'table-tree']" />
                <span class="menuText">
                  {{ $t("league-management") }}
                  <span class="beta-badge">NEW</span>
                </span>
              </router-link>
            </div>
          </div>
          <!-- <div class="menuSection p-grid">
            <div class="p-col">
              <router-link to="/timeslots" class="menuHeader blueHover">
                <font-awesome-icon class="menuIcon" :icon="['fal', 'clock']" />
                <span class="menuText"> Timeslots Calendar </span>
              </router-link>
            </div>
          </div> -->
          <div class="menuSection p-grid">
            <!-- <div class="p-col-fixed iconCol" @click.stop="toggleActive('divisions')">
            <font-awesome-icon
              class="menuIcon angleIcon"
              :icon="isActive('divisions') ? ['fal', 'angle-up']
                      : ['fal', 'angle-down']"
            />
          </div> -->
            <div class="p-col">
              <div class="menuHeader blueHover" @click.stop="toggleActive('divisions')">
                <font-awesome-icon class="menuIcon" :icon="['fal', 'ball-pile']" />
                <span class="menuText"> {{ $t("AppMenu.divisions") }} </span>
                <font-awesome-icon
                  class="menuIcon angleIcon"
                  :icon="isActive('divisions') ? ['fal', 'angle-up'] : ['fal', 'angle-down']"
                />
              </div>
              <div v-if="isActive('divisions')">
                <div v-for="d in currentSeason.divisions" :key="d.id" class="menuSection">
                  <router-link :to="`/divisions/${d.id}`" style="font-size: 1.1em; font-weight: bold">
                    {{ d.name }}
                    <span v-if="d.shortcode && d.shortcode.length"> ({{ d.shortcode }})</span>
                  </router-link>
                  <div v-for="t in d.teams" :key="t.id" class="p-my-2">
                    <router-link :to="`/teams/${t.id}`">
                      <div class="menuEntry blueHover" :style="{ background: getBackground('/teams/' + t.id) }">
                        <div class="menuEntryColor" v-bind:style="{ background: d.color || '#FFFFFF' }"></div>
                        <span class="menuEntryName">
                          {{ t.name }}
                          <span v-if="d.is_across && (t.division_abbreviation || t.division_name)">
                            ({{ t.division_abbreviation || t.division_name }})
                          </span>
                        </span>
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="menuSection p-grid">
            <div class="p-col">
              <p class="menuHeader blueHover" @click.stop="toggleActive('venues')">
                <font-awesome-icon class="menuIcon" :icon="['fal', 'map-marker-alt']" />
                <span class="menuText"> &nbsp; {{ $t("AppMenu.venues") }} </span>
                <font-awesome-icon
                  class="menuIcon angleIcon"
                  :icon="isActive('venues') ? ['fal', 'angle-up'] : ['fal', 'angle-down']"
                />
              </p>
              <div v-if="isActive('venues')" class="menuSection">
                <div v-for="d in currentSeason.venues" :key="d.id" class="p-my-1">
                  <router-link :to="`/venues/${d.id}`">
                    <div class="menuEntry blueHover" :style="{ background: getBackground('/venues/' + d.id) }">
                      <div class="menuEntryColor" v-bind:style="{ background: d.color || '#FFFFFF' }"></div>
                      <span class="menuEntryName">{{ d.name }}</span>
                    </div>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="menuSection p-grid">
          <div class="p-col">
            <p class="menuHeader blueHover" @click.stop="toggleActive('people')">
              <font-awesome-icon class="menuIcon" :icon="['fal', 'users']" />
              <span class="menuText"> {{ $t("AppMenu.people") }} </span>
              <font-awesome-icon
                class="menuIcon angleIcon"
                :icon="isActive('people') ? ['fal', 'angle-up'] : ['fal', 'angle-down']"
              />
            </p>
            <div v-if="isActive('people')" class="menuSection">
              <div v-for="d in currentSeason.people" :key="d.id">
                <router-link :to="`/people/${d.id}`">
                  <div class="menuEntry">
                    <div class="menuEntryColor" v-bind:style="{ background: d.color || '#FFFFFF' }"></div>
                    <span class="menuEntryName">{{ d.first + " " + d.last }}</span>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="menuSection p-grid" v-if="seConnected & orgOwner & !jpLocale">
          <div class="p-col">
            <p class="menuHeader blueHover" @click.stop="goSportsEngine">
              <img src="/se_logo_small.png" style="height: 1em; vertical-align: middle" />
              <span class="menuText ml-2"> {{ $t("AppMenu.se") }} </span>
            </p>
            <div v-if="isActive('people')" class="menuSection">
              <div v-for="d in currentSeason.people" :key="d.id">
                <router-link :to="`/people/${d.id}`">
                  <div class="menuEntry">
                    <div class="menuEntryColor" v-bind:style="{ background: d.color || '#FFFFFF' }"></div>
                    <span class="menuEntryName">{{ d.first + " " + d.last }}</span>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="menuSection p-grid">
          <div class="p-col">
            <p class="menuHeader blueHover" @click.stop="toggleActive('account')">
              <font-awesome-icon class="menuIcon" :icon="['fal', 'cog']" />
              <span class="menuText"> &nbsp; {{ $t("AppMenu.settings") }} </span>
              <font-awesome-icon
                class="menuIcon angleIcon"
                :icon="isActive('account') ? ['fal', 'angle-up'] : ['fal', 'angle-down']"
              />
            </p>
            <div v-if="isActive('account')">
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/leagues')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.manage_league") }}</span>
              </div>
              <!-- <br /> -->
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/account')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.manage_account") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/organization')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.manage_organization") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/billing')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.billing") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="startSportsEngineAuth" v-if="orgOwner && !jpLocale">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.se_connect") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/wizard')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.wizard") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/import/csv')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.import_csv") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/import')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.import_json") }}</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="goRouter('/bulk')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.bulk_edit") }}</span>
              </div>
              <div v-if="isAdmin" class="menuEntry p-my-2 p-ml-3" @click="goRouter('/dropshadows')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">DropShadows</span>
              </div>
              <div class="menuEntry p-my-2 p-ml-3" @click="signOut">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.sign_out") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <Menu :model="items" /> -->
    </div>
    <div v-else-if="busy">
      <ProgressSpinner />
    </div>
    <div v-else>
      <div id="menuWrapper">
        <div class="menuSection" @click="goRouter('/')">
          <div id="logoBlock">
            <img
              @click="refreshLeagueInformation"
              src="https://diamond-public.s3.us-west-2.amazonaws.com/assets/diamond-logo-white.png"
            />
          </div>
        </div>
        <div class="menuSection p-grid">
          <!-- <div class="p-col-fixed iconCol"></div> -->
          <div class="p-col">
            <Button
              @click="toggleCreateDialog"
              class="
                p-button-raised p-button-lg p-button-text
                defaultColor
                p-button-rounded p-my-2
              "
            >
              <font-awesome-icon class="menuIcon dsColor" icon="plus" />
              &nbsp;
              <strong class="uppercase">{{ $t("AppMenu.create_button") }}</strong> &nbsp;
            </Button>
          </div>
        </div>
        <div class="menuSection p-grid">
          <!-- <div class="p-col-fixed iconCol" @click.stop="toggleActive('account')">
            <font-awesome-icon
              class="menuIcon angleIcon"
              :icon="isActive('account') ? ['fal', 'angle-up']
                      : ['fal', 'angle-down']"
            />
          </div> -->
          <div class="p-col">
            <p class="menuHeader blueHover" @click.stop="toggleActive('account')">
              <font-awesome-icon class="menuIcon" icon="cog" />
              <span class="menuText"> {{ $t("AppMenu.account") }} </span>
              <font-awesome-icon
                class="menuIcon angleIcon"
                :icon="isActive('account') ? ['fal', 'angle-up'] : ['fal', 'angle-down']"
              />
            </p>
            <div v-if="isActive('account')">
              <a class="menuEntry" @click="goRouter('/billing')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.billing") }}</span>
              </a>
              <br />
              <a class="menuEntry" @click="startSportsEngineAuth">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.link_se") }}</span>
              </a>
              <br />
              <a class="menuEntry" @click="goRouter('/wizard')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.wizard") }}</span>
              </a>
              <br />
              <a class="menuEntry" @click="goRouter('/import')">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.import_json") }}</span>
              </a>
              <div class="menuEntry" @click="signOut">
                <div class="menuEntryColor"></div>
                <span class="menuEntryName">{{ $t("AppMenu.sign_out") }}</span>
              </div>
              <small v-if="isDebug">
                Debug User:
                {{ $store.getters.user }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["test"],
  // ddddd
  data: function() {
    return {
      busy: true,
      createDialog: false,
      editing: true,
      activePanels: ["create", "view"],
    };
  },
  computed: {
    currentOrganizationToggle() {
      if (this.busy) {
        return false;
      }
      return this.$store.getters.currentOrganization;
    },
    currentLeagueToggle() {
      if (this.busy) {
        return false;
      }
      return this.$store.getters.currentLeague;
    },
    currentSeasonToggle() {
      if (this.busy) {
        return false;
      }
      return this.$store.getters.currentSeason;
    },
    leagueItems() {
      var items = [];
      var leagues = [];
      this.leagues.forEach(league => {
        leagues.push({
          label: league.name,
          command: () => {
            this.setCurrentLeague(league);
          },
        });
      });
      leagues.push({
        label: this.$t("AppMenu.new-league"),
        icon: "pi pi-plus",
        command: () => {
          this.goRouter("/leagues/new");
        },
      });
      items.push({
        label: this.currentLeagueToggle.name,
        items: leagues,
      });
      var seasons = [];
      this.seasons.forEach(season => {
        seasons.push({
          label: season.name,
          command: () => {
            this.setCurrentSeason(season);
            this.goRouter(`/`);
          },
        });
      });
      seasons.push({
        label: this.$t("AppMenu.new-season"),
        icon: "pi pi-plus",
        command: () => {
          this.goRouter("/seasons/new");
        },
      });
      items.push({
        label: this.currentSeasonToggle.name,
        items: seasons,
      });
      return items;
    },
    items() {
      var items = [];
      items.push({
        label: this.$t("AppMenu.scheduling"),
        items: [
          {
            label: this.$t("AppMenu.timeslot_calendar"),
            to: "/timeslots",
          },
          {
            label: this.$t("AppMenu.master-games-calendar"),
            to: "/calendar",
          },
        ],
      });

      //
      items.push({
        label: this.$t("Division.divisions"),
        items: this.seasonItems.divisions,
      });

      console.log(this.$store.getters.currentSeason);
      this.$store.getters.currentSeason.divisionTeams.forEach(d => {
        var x = [];
        d.teams.forEach(t => {
          x.push({
            label: t.name,
            to: `/teams/${t.id}`,
            // to: `/teams/${t.name.toLowerCase()}`,
          });
        });
        x.push({
          label: this.$t("AppMenu.new-team"),
          icon: "pi pi-plus",
          command: () => {
            this.goRouter(`/divisions/${d.id}/teams/new`);
          },
        });
        items.push({
          label: d.name,
          items: x,
        });
      });

      // items.push({
      //   label: "Teams",
      //   items: this.seasonItems.teams,
      // });

      items.push({
        label: this.$t("Venue.venues"),
        items: this.seasonItems.venues,
      });
      items.push({
        label: this.$t("People.people"),
        items: this.seasonItems.people,
      });
      items.push({
        label: this.$t("Account.account"),
        items: [
          {
            label: this.$t("sign-out"),
            command: () => {
              const currentLocale = JSON.parse(localStorage.getItem("locale")) || 'en';
              this.$store.dispatch("signOut").then(() => {                
                this.goRouter(`/${currentLocale}/sign-in`);
                this.makeToast("info", this.$t("Toast.signed-out"));
              });
            },
          },
        ],
      });
      return items;
      // items: [
      //   {
      //     label: "Divisions",
      //     items: this.divisonItems,
      //   },
      //   {
      //     label: "Teams",
      //     items: [{ label: "A", icon: "pi pi-fw pi-plus", command: () => {} }],
      //   },
      //   {
      //     label: "Venues",
      //     items: [{ label: "A", icon: "pi pi-fw pi-plus", command: () => {} }],
      //   },
      //   {
      //     label: "People",
      //     items: [{ label: "A", icon: "pi pi-fw pi-plus", command: () => {} }],
      //   },
      //   {
      //     label: "Timeslots",
      //     items: [{ label: "A", icon: "pi pi-fw pi-plus", command: () => {} }],
      //   },
      //   {
      //     label: "Account",
      //     items: [
      //       { label: "Options", icon: "pi pi-fw pi-cog", to: "/options" },
      //       { label: "Sign Out", icon: "pi pi-fw pi-power-off", to: "/logout" },
      //     ],
      //   },
      // ],
    },
    currentLeague() {
      return this.$store.getters.currentLeague || {};
    },
    currentSeason() {
      return this.$store.getters.currentSeason;
    },
    currentOrganization() {
      return this.$store.getters.currentOrganization || {};
    },
    leagues() {
      return this.$store.getters.leagueInformation;
    },
    organizations() {
      return this.$store.getters.organizationInformation;
    },
    seasons() {
      return this.currentLeague.seasons || [];
    },
    seasonItems() {
      var items = {
        divisions: [],
        teams: [],
        venues: [],
        people: [],
      };
      if (!this.currentSeason) {
        return items;
      }

      this.currentSeason.divisions.forEach(d => [
        items.divisions.push({
          label: d.name,
          id: d.id,
          to: "/divisions/" + d.id,
          // to: "/divisions/" + d.name.toLowerCase(),
        }),
      ]);
      items.divisions.push({
        label: this.$t("AppMenu.new-division"),
        icon: "pi pi-plus",
        command: () => {
          this.goRouter("/divisions/new");
        },
      });

      this.currentSeason.teams.forEach(d => [
        items.teams.push({
          label: d.name,
          id: d.id,
          to: "/teams/" + d.id,
          // to: "/teams/" + d.name.toLowerCase(),
        }),
      ]);

      this.currentSeason.venues.forEach(d => [
        items.venues.push({
          label: d.name,
          id: d.id,
          style: "margin-left: 10px; border: 1px solid red",
          to: "/venues/" + d.id,
          // to: "/venues/" + d.name.toLowerCase(),
        }),
      ]);
      items.venues.push({
        label: this.$t("AppMenu.new_venue"),
        icon: "pi pi-plus",
        command: () => {
          this.goRouter("/venues/new");
        },
      });

      this.currentSeason.people.forEach(d => [
        items.people.push({
          label: d.name,
          id: d.id,
          to: "/people/" + d.id,
        }),
      ]);
      items.people.push({
        label: this.$t("AppMenu.new-person"),
        icon: "pi pi-plus",
        command: () => {
          this.goRouter("/people/new");
        },
      });
      return items;
    },
  },
  methods: {
    goSportsEngine() {
      this.refreshLeagueInformation("/sportsengine");
    },
    getBackground(path) {
      if (this.$route.path === path) {
        return "rgba(160, 215, 254, 0.3)";
      } else {
        return "none";
      }
    },
    toggleCreateDialog() {
      this.createDialog = !this.createDialog;
    },
    toggleActive(id) {
      if (this.isActive(id)) {
        this.activePanels.splice(this.activePanels.indexOf(id), 1);
      } else {
        this.activePanels.push(id);
      }
    },
    isActive(id) {
      return this.activePanels.indexOf(id) > -1;
    },
    signOut() {
      const currentLocale = JSON.parse(localStorage.getItem("locale"));

      this.$store.dispatch("signOut").then(() => {        
        this.goRouter(`/${currentLocale}/sign-in`);
        this.makeToast("info", this.$t("Toast.signed-out"));
      });
    },
    setCurrentOrganization(organizationId) {
      const organization = this.organizations.find(o => o.id === organizationId);
      this.switchOrganization(organization);
      this.makeToast("info", this.$t("Toast.switch-to", { organization: organization.name }));
    },
    setCurrentLeague(league) {
      // this.editing = false;
      this.$store.dispatch("setCurrentLeague", league);
      if (league.seasons[0]) {
        this.setCurrentSeason(league.seasons[0]);
        this.dropdownSeason = league.seasons[0];
      } else {
        // this.goRouter("/setup");
      }
    },
    setCurrentSeason(season) {
      // this.editing = false;
      this.$store.dispatch("setCurrentSeason", season);
      console.log("🌎 SCS: " + season.id);
      this.refreshLeagueInformation();
      this.goRouter("/");
    },
  },
  watch: {
    $route: function(id) {
      this.createDialog = false;
    },
  },
  mounted() {
    if (this.currentSeason) {
      this.busy = false;
      return;
    } else {
      this.refreshLeagueInformation();
      setTimeout(() => {
        this.busy = false;
        if (!this.currentSeasonToggle) {
          console.log("No current season toggle");
          this.refreshLeagueInformation();
        }
      }, 1000);
    }
  },
};
</script>

<style scoped lang="scss">
.blueHover {
  &:hover {
    background: #a0d7fe !important;
    // margin-left: -5px;
  }
}
.menuHeader {
  width: 100%;
  display: block;
  padding: 10px;
  font-size: 1.25em;
  font-weight: normal;
  color: #4a4a4a !important;
  cursor: pointer;
  border-radius: 5px;
}
.createEntry {
  font-size: 1.5em;
  cursor: pointer;
  margin-left: 5px;
  margin-bottom: 15px;
  .menuIcon {
    width: 30px;
    margin-right: 10px;
  }
  // padding: 5px 15px;
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 5px;
  }
}
.p-col {
  padding: 0px;
}
#logoBlock {
  text-align: left;
  img {
    width: 200px;
  }
}
.iconCol {
  width: 15px;
  padding: 0px;
}

.angleIcon {
  float: right;
  // height: 3.5em;
  // margin-top: 1em;
  margin-left: 5px;
  font-size: 1.5em;
  vertical-align: middle;
}
#menuWrapper {
  text-align: left;
}

.menuSection {
  margin: 10px auto;
  padding: 0px 30px;
  cursor: pointer;
}

.menuIcon {
  margin-right: 7px;
}

.menuEntry {
  // padding: 5px;
  font-size: 1.2em;
  border-radius: 5px;
  padding-left: 5px;
  &:hover {
    background: #e9ecef;
    border-radius: 5px;
  }

  .pi {
    font-size: 0.75em;
    color: #495057;
  }
}

.menuEntryName {
  margin-left: 5px;
  color: #495057;
}
.halfEntry {
  width: 50%;
  display: inline-block;
}
</style>
